import {useMemo} from "react";
import type {
  GroupRepo,
  IngredientId,
  RecipeId,
  ScheduledRecipeEntry,
  ScheduledRecipeId,
} from "./GroupRepo";
import {useIndex, useMultiKeyIndex} from "./use-index";
import {useSortedResultRowIds} from "./use-query";

export const useSortedIngredientIds = (groupRepo: GroupRepo, recipeId: RecipeId) => {
  return useIndex<IngredientId>(recipeId, groupRepo.indexes, "sortedIngredients");
};

export const useSortedIngredientSlices = (groupRepo: GroupRepo, recipeIds: Set<RecipeId>) => {
  return useMultiKeyIndex<RecipeId, IngredientId>(
    recipeIds,
    groupRepo.indexes,
    "sortedIngredients"
  );
};

export const getIngredientIdsForRecipe = (groupRepo: GroupRepo, recipeId: RecipeId) => {
  return groupRepo.indexes.getSliceRowIds("sortedIngredients", recipeId) as IngredientId[];
};

export const useSortedIngredients = (groupRepo: GroupRepo, recipeId: RecipeId) => {
  const ids = useSortedIngredientIds(groupRepo, recipeId);
  const all = groupRepo.Ingredients.useGetAll();
  return useMemo(() => ids.map((id) => all[id]), [ids, all]);
};

export const getScheduledRecipes = (groupRepo: GroupRepo) => {
  return Object.values(groupRepo.queries.getResultTable("incompleteScheduledRecipes")).map(
    (rawVariant) => groupRepo.ScheduledRecipes.hydrate(rawVariant)
  );
};

export const useScheduledRecipeIds = (groupRepo: GroupRepo) =>
  useSortedResultRowIds<ScheduledRecipeId>(
    groupRepo.queries,
    "incompleteScheduledRecipes",
    "scheduledAt",
    true,
    0,
    undefined
  );

export const useSortedRecipeIds = (groupRepo: GroupRepo) => {
  return useIndex<RecipeId>("", groupRepo.indexes, "sortedRecipes");
};

export const useSortedRecipes = (groupRepo: GroupRepo) => {
  const ids = useSortedRecipeIds(groupRepo);
  const unitEntries = groupRepo.Recipes.useGetAll();
  return ids.map((id) => unitEntries[id]);
};

export type CookStats = {total: number; lastYear: number; lastCookedAt: Date | null};

// Applying the potlist adds a scheduledRecipes row per recipe, and rows stay after being checked
// off, so they're the cooking log. Counting rows rather than keeping a counter cell means
// devices that cook offline at the same time don't overwrite each other's counts when they merge.
export const getCookStats = (scheduled: ScheduledRecipeEntry[], now: Date) => {
  const yearAgo = new Date(now);
  yearAgo.setFullYear(now.getFullYear() - 1);
  const statsById = new Map<RecipeId, CookStats>();
  for (const {recipeId, scheduledAt} of scheduled) {
    let stats = statsById.get(recipeId);
    if (!stats) {
      stats = {total: 0, lastYear: 0, lastCookedAt: null};
      statsById.set(recipeId, stats);
    }
    stats.total += 1;
    if (scheduledAt >= yearAgo) stats.lastYear += 1;
    if (!stats.lastCookedAt || scheduledAt > stats.lastCookedAt) stats.lastCookedAt = scheduledAt;
  }
  return statsById;
};

export const useCookStats = (groupRepo: GroupRepo) => {
  const scheduled = groupRepo.ScheduledRecipes.useGetAll();
  return useMemo(() => getCookStats(Object.values(scheduled), new Date()), [scheduled]);
};
